import { Box, Container, Typography, Grid, TextField, Button, Card, useTheme } from '@mui/material';
import { motion } from 'framer-motion';
import EmailIcon from '@mui/icons-material/Email';
import ChatIcon from '@mui/icons-material/Chat';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { useState } from 'react';
import { contactUsEmail } from '../services/api';

const Contact = () => {
  const theme = useTheme();
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    message: '',
  });
  const [submitStatus, setSubmitStatus] = useState({ type: '', message: '' });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitStatus({ type: '', message: '' });

    try {
      const response = await contactUsEmail(
        formData.first_name,
        formData.last_name,
        formData.email,
        formData.phone,
        formData.message
      );

      // Check if backend returned error
      if (response.status === 'error') {
        setSubmitStatus({ 
          type: 'error', 
          message: response.message || 'Failed to send message' 
        });
        return;
      }

      // Success - clear form and show success message
      setSubmitStatus({ 
        type: 'success', 
        message: 'Message sent successfully!' 
      });
      setFormData({
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        message: ''
      });
    } catch (error) {
      setSubmitStatus({ 
        type: 'error', 
        message: error.response?.data?.message || error.message || 'Failed to send message. Please try again.' 
      });
    }
  };

  const contactInfo = [
    {
      icon: <ChatIcon sx={{ fontSize: 40 }} />,
      title: 'Phone',
      info: '1(833) 838-8398',
      subInfo: '1(833) 838-VFYV',
      color: theme.palette.primary.main,
    },
    {
      icon: <EmailIcon sx={{ fontSize: 40 }} />,
      title: 'Email',
      info: 'info@vfyv.org',
      subInfo: 'We reply within 24 hours',
      color: theme.palette.secondary.main,
    },
    {
      icon: <LocationOnIcon sx={{ fontSize: 40 }} />,
      title: 'Locations',
      info: 'National Coverage',
      subInfo: 'We can assist you on a National Level',
      color: '#0066CC',
    },
  ];

  return (
    <Box sx={{ width: '100%', overflow: 'hidden' }}>
      {/* Hero Section */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`,
          pt: { xs: '80px', sm: '100px', md: '140px' },
          pb: { xs: '100px', sm: '120px', md: '140px' }, // Increased bottom padding
          px: { xs: 3, sm: 4, md: 0 },
          color: 'white',
          position: 'relative',
          marginBottom: { xs: 4, sm: 6, md: 8 }, // Added margin bottom
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'url("/pattern.png")',
            opacity: 0.05,
            zIndex: 1,
            backgroundColor: 'rgba(0, 0, 0, 0.3)',
          },
        }}
      >
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Typography 
              variant="h1" 
              sx={{ 
                fontSize: { xs: '2.5rem', sm: '3rem', md: '3.5rem' },
                fontWeight: 800,
                mb: { xs: 3, md: 4 },
                textAlign: 'center',
                textShadow: '2px 2px 8px rgba(0,0,0,0.3)',
                letterSpacing: '0.5px',
              }}
            >
              Contact Us
            </Typography>
            <Typography 
              variant="h5" 
              align="center"
              sx={{ 
                maxWidth: 800,
                mx: 'auto',
                opacity: 1,
                lineHeight: 1.6,
                fontSize: { xs: '1.1rem', sm: '1.25rem', md: '1.5rem' },
                px: { xs: 2, sm: 0 },
                textShadow: '1px 1px 4px rgba(0,0,0,0.2)',
              }}
            >
              Have questions about vehicle donation? We&apos;re here to help!
            </Typography>
          </motion.div>
        </Container>
      </Box>

      {/* Contact Form */}
      <Box
        sx={{
          py: { xs: 8, md: 10 }, // Increased vertical padding
          background: theme.palette.grey[50],
          position: 'relative',
          mt: { xs: -10, sm: -12, md: -14 }, // Adjusted negative margin to match new padding
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '100%',
            background: `linear-gradient(45deg, ${theme.palette.primary.main}05 25%, transparent 25.1%)`,
          },
        }}
      >
        <Container maxWidth="md">
          <Card
            elevation={0}
            sx={{
              p: { xs: 3, md: 6 },
              borderRadius: '20px',
              background: 'white',
              border: `1px solid ${theme.palette.grey[200]}`,
            }}
          >
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <Typography variant="h3" gutterBottom align="center" sx={{ fontWeight: 700 }}>
                Send Us a Message
              </Typography>
              <Typography 
                variant="body1" 
                align="center" 
                sx={{ 
                  mb: 4,
                  color: 'text.secondary',
                  maxWidth: 600,
                  mx: 'auto',
                }}
              >
                Fill out the form below and we&apos;ll get back to you as soon as possible.
              </Typography>

              <form onSubmit={handleSubmit}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="First Name"
                      name="first_name"
                      value={formData.first_name}
                      onChange={handleInputChange}
                      variant="outlined"
                      required
                      sx={{ mb: { xs: 2, md: 0 } }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Last Name"
                      name="last_name"
                      value={formData.last_name}
                      onChange={handleInputChange}
                      variant="outlined"
                      required
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      variant="outlined"
                      required
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      variant="outlined"
                      required
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Message"
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      variant="outlined"
                      multiline
                      rows={4}
                      required
                    />
                  </Grid>
                  {submitStatus.message && (
                    <Grid item xs={12}>
                      <Typography
                        color={submitStatus.type === 'error' ? 'error' : 'success'}
                        textAlign="center"
                      >
                        {submitStatus.message}
                      </Typography>
                    </Grid>
                  )}
                  <Grid item xs={12}>
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      size="large"
                      fullWidth
                      sx={{
                        py: 1.5,
                        fontSize: '1.1rem',
                        borderRadius: '50px',
                        textTransform: 'none',
                      }}
                    >
                      Send Message
                    </Button>
                  </Grid>
                </Grid>
              </form>
            </motion.div>
          </Card>
        </Container>
      </Box>

      {/* Contact Info Cards */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Grid container spacing={4}>
          {contactInfo.map((info, index) => (
            <Grid item xs={12} md={4} key={index}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.2 }}
              >
                <Card
                  elevation={2}
                  sx={{
                    p: 4,
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    textAlign: 'center',
                    background: 'white',
                    borderRadius: '16px',
                    transition: 'transform 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 80,
                      height: 80,
                      borderRadius: '20px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 3,
                      background: `linear-gradient(135deg, ${info.color} 0%, ${info.color}dd 100%)`,
                      color: 'white',
                      transform: 'rotate(-10deg)',
                    }}
                  >
                    {info.icon}
                  </Box>
                  <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                    {info.title}
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 1, color: theme.palette.primary.main }}>
                    {info.info}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {info.subInfo}
                  </Typography>
                </Card>
              </motion.div>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

export default Contact;
